import { useAuth0 } from "@auth0/auth0-react";
import { PropsWithChildren, useEffect, useState } from "react";

import useBasicData from "../../hooks/useBasicData";
import {
  DAY_IN_MILLISECONDS,
  WEEK_IN_MILLISECONDS,
} from "../../utils/constants";
import TeamNotices from "./TeamNotices";

interface Notice {
  isLive: boolean;
  content: string;
  updatedAt: string;
  frequency: "ONCE" | "DAILY" | "WEEKLY" | "PER_LOGIN";
}

const shouldShowNotice = (
  notice: Notice,
  lastSeenAt: Date | undefined,
  lastLoginAt: Date | null,
): boolean => {
  if (!notice.isLive || !notice.content || notice.content.length === 0) {
    return false;
  }
  const noticeDate = new Date(notice.updatedAt);
  if (!lastSeenAt || noticeDate > lastSeenAt) {
    return true;
  }

  const now = new Date().getTime();
  switch (notice.frequency) {
    case "ONCE":
      return false;
    case "DAILY":
      return (
        lastSeenAt > noticeDate &&
        now - lastSeenAt.getTime() > DAY_IN_MILLISECONDS
      );
    case "WEEKLY":
      return (
        lastSeenAt > noticeDate &&
        now - lastSeenAt.getTime() > WEEK_IN_MILLISECONDS
      );
    case "PER_LOGIN":
      return !lastLoginAt || lastLoginAt > lastSeenAt;
  }
  return false;
};

const TeamNoticesWrapper = ({ children }: PropsWithChildren) => {
  const { getIdTokenClaims } = useAuth0();
  const { workspace } = useBasicData({ fetch: true });
  const [lastLoginAt, setLastLoginAt] = useState<Date | null>(null);

  useEffect(() => {
    const checkLoginClaims = async () => {
      const claims = await getIdTokenClaims();
      if (claims?.iat) {
        setLastLoginAt(new Date(claims.iat * 1000));
      }
    };
    checkLoginClaims();
  }, [getIdTokenClaims]);

  const unseenNotices = (workspace?.notices?.notices as Notice[] | undefined)
    ?.filter((notice) =>
      shouldShowNotice(
        notice,
        workspace?.myNoticeDismissedAt
          ? new Date(workspace.myNoticeDismissedAt)
          : undefined,
        lastLoginAt,
      ),
    )
    .map((notice) => notice.content);

  if (workspace && unseenNotices?.length) {
    return (
      <TeamNotices
        workspaceId={workspace.id}
        workspaceName={workspace.name}
        workspaceLogo={workspace.profilePicture ?? ""}
        notices={unseenNotices}
      />
    );
  }

  return children;
};

export default TeamNoticesWrapper;
